import styled from "styled-components";
import { useContext } from "react";
import { ReactComponent as ManifestLogo } from "manifest-components/src/Images/icons/manifest-gray.svg";
import { ColorVariables } from "manifest-components/src/Colors";
import { Link, LinkProps, useLocation } from "react-router-dom";
import Popover from "manifest-components/src/Popover/Popover";
import { AppContext } from "../../context/App/appProvider";
import { RoutePaths, useTypedNavigate } from "../../routes/RoutePaths";
import useUser from "../../hooks/useUser";
import Authentication from "../../utils/authentication";
import useOnboardingDetails from "../../hooks/useOnboardingDetails";

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  .no-underline {
    text-decoration: none;
  }
  .manifestLogo {
    position: relative;
  }
  .separator {
    width: 2px;
    height: 31px;
    background: rgba(64, 63, 60, 0.2);
    margin: 0 12px;
  }
  .employerName {
    font-weight: 600;
    font-size: 18px;
    line-height: 32px;
    margin-right: 6px;
  }
  .dashboardTitle {
    color: rgba(46, 45, 41, 0.5);
    font-size: 18px;
    line-height: 32px;
  }
  .links {
    margin-left: auto;
    a {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: ${ColorVariables.coco};
      margin-right: 50px;
      text-decoration: none;
      position: relative;
      &.active {
        color: ${ColorVariables.mint};
        &:after {
          content: " ";
          height: 2px;
          background: ${ColorVariables.mint};
          position: absolute;
          top: 48px;
          left: 0;
          right: 0;
        }
      }
      &:last-child {
        margin-right: 0;
      }
    }
  }
  .profile {
    margin-left: 80px;
    line-height: 25px;
    padding: 4px;
    text-align: center;
    color: ${ColorVariables.cream};
    font-size: 14px;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    border: 4px solid ${ColorVariables.mint3};
    background: linear-gradient(0deg, #48b7a9, #48b7a9), linear-gradient(0deg, #4c9e94, #4c9e94),
      linear-gradient(0deg, #4c9e94, #4c9e94), #4c9e94;
  }
  .dashboardLink {
    display: flex;
  }
`;

function CustomLink({ children, to, ...props }: LinkProps) {
  const location = useLocation();
  const match = location.pathname.indexOf(`${to}`) > -1;
  return (
    <Link className={match ? "active" : ""} to={to} {...props}>
      {children}
    </Link>
  );
}

export default function HeaderAuth({ isVerified = true }: { isVerified?: boolean }) {
  const { user } = useUser();
  const { openHamburgerMenu } = useContext(AppContext);
  const { onboardingDetails } = useOnboardingDetails();
  const navigate = useTypedNavigate();

  const handleViewDashboard = () => {
    navigate(RoutePaths.dashboard);
  };

  const handleSignOut = () => {
    openHamburgerMenu();
    Authentication.clear();
    navigate(RoutePaths.welcome);
  };

  return (
    <StyledHeader>
      <CustomLink to={RoutePaths.dashboard}>
        <ManifestLogo className="manifestLogo" />
      </CustomLink>
      <div className="separator" />
      <button className="dashboardLink" type="button" onClick={handleViewDashboard}>
        <div className="employerName">{user?.companyName}</div>
        <div className="dashboardTitle">dashboard</div>
      </button>
      <div className="links">
        {isVerified && (
          <>
            {onboardingDetails?.completed && user?.editPermission && (
              <CustomLink to={RoutePaths.settings}>Settings</CustomLink>
            )}
            <CustomLink to={RoutePaths.resources}>Resources</CustomLink>
            <CustomLink to={RoutePaths.support}>Support</CustomLink>
          </>
        )}
      </div>
      <Popover
        style={{ width: 200, marginLeft: -80, marginTop: 10 }}
        caret="top-right"
        content={
          <button data-name="initials" name="initials" className="profile">
            {user?.initials}
          </button>
        }
        popoverContent={
          <Popover.Links>
            {isVerified && (
              <>
                <CustomLink to={RoutePaths.account}>Your profile</CustomLink>
                <CustomLink to={RoutePaths.accountChangePassword}>Change password</CustomLink>
              </>
            )}
            <button data-name="logout" onClick={handleSignOut}>
              Log out
            </button>
          </Popover.Links>
        }
      />
    </StyledHeader>
  );
}
